import { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useSearchParams } from "react-router-dom";
import {
  ImageIcon,
  Shuffle,
  Camera,
  Sparkles,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AnalyticsService } from "@/services/analytics";
import { getRandomPrompt } from "@/lib/prompt-suggestions";
import {
  GenerateButton,
  PromptInput,
  UpgradePlansModal,
  AuthModal,
  MobileBottomBar,
} from "@/components/ui";
import {
  ImageDimensionsDropdown,
  type ImageDimension,
} from "./image-dimensions-dropdown";
import { POSITION_PRESETS, type PositionPreset } from "./position-presets";
import { STYLE_PRESETS, type StylePreset } from "./style-presets";
import { PiecanNotice } from "@/components/features/piecan-notice";
import { useStartGeneration, useActiveGenerations } from "@/hooks";
import { useUserStore, hasAccessToPlanLevel } from "@/stores/user-store";

const IMAGE_CREDIT_COST = 5;

const ASPECT_RATIOS: Record<ImageDimension, string> = {
  "1024x1024": "1:1",
  "1024x1536": "2:3",
  "1536x1024": "3:2",
  "864x1536": "9:16",
  "1536x864": "16:9",
  "960x1280": "3:4",
  "1280x960": "4:3",
};

const STYLE_ICONS: Record<string, React.ReactNode> = {
  realistic: <Camera className="size-4" />,
  anime: <Sparkles className="size-4" />,
};

export function TextToImageForm() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSignedIn } = useUser();
  const { credits, plan } = useUserStore();
  const { startGeneration } = useStartGeneration();
  const { hasActiveGeneration } = useActiveGenerations();

  const [prompt, setPrompt] = useState(() => searchParams.get("prompt")?.trim() ?? "");
  const [selectedPosition, setSelectedPosition] = useState<PositionPreset | null>(
    null,
  );
  const [selectedStyle, setSelectedStyle] = useState<StylePreset>(
    STYLE_PRESETS.find((s) => s.id === "realistic")!,
  );
  // Default to portrait (9:16) — character generations are vertical-first
  // in this product, and most downstream I2V uses inherit the same aspect.
  const [dimensions, setDimensions] = useState<ImageDimension>("864x1536");
  const [promptOptimization, setPromptOptimization] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    preset: "no-credits" | "concurrent-limit";
    creditsNeeded?: number;
  }>({ open: false, preset: "no-credits" });
  const [showAuthModal, setShowAuthModal] = useState(false);

  const hasUnlimitedConcurrent = hasAccessToPlanLevel(plan, "pro");

  useEffect(() => {
    if (!searchParams.has("prompt")) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("prompt");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleGenerate = () => {
    if (!prompt.trim()) return;

    if (!isSignedIn) {
      setShowAuthModal(true);
      return;
    }

    if (credits < IMAGE_CREDIT_COST) {
      AnalyticsService.trackOutOfCredits({
        credits_remaining: credits,
        credits_required: IMAGE_CREDIT_COST,
        model_attempted: "qwen_image_lora",
        generation_type: "image",
        is_paid_user: plan !== "free",
        user_plan: plan,
      });
      setUpgradeModal({
        open: true,
        preset: "no-credits",
        creditsNeeded: IMAGE_CREDIT_COST,
      });
      return;
    }

    if (hasActiveGeneration && !hasUnlimitedConcurrent) {
      setUpgradeModal({ open: true, preset: "concurrent-limit" });
      return;
    }

    let finalPrompt = prompt.trim();
    if (selectedPosition?.readableName) {
      finalPrompt = `${finalPrompt}, ${selectedPosition.readableName}`;
    }

    startGeneration({
      modelId: "qwen_image_lora",
      prompt: prompt.trim(),
      type: "image",
      generateParams: {
        model_id: "qwen_image_lora",
        prompt: finalPrompt,
        aspect_ratio: ASPECT_RATIOS[dimensions],
        prompt_enhancer: promptOptimization,
        style: selectedStyle.optimizerStyle,
        position_trigger_word: selectedPosition?.triggerWord || undefined,
      },
    });
  };

  return (
    <>
      <div className="space-y-5 pb-24 md:pb-0">
        {/* Partner upsell — points upload-capable users to PieCan for the
            image-upload flow. Compact so the composer stays primary. */}
        <PiecanNotice variant="compact" source="t2i_form" />

        <PromptInput
          value={prompt}
          onChange={setPrompt}
          placeholder="Describe what you want to create..."
        />

        <button
          type="button"
          onClick={() => setPrompt(getRandomPrompt())}
          className="flex items-center gap-1.5 text-xs text-[#666] hover:text-white transition-colors"
        >
          <Shuffle className="size-3.5" />
          Surprise me
        </button>

        {/* Style Presets */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-[#888]">Style</label>
          <div className="flex gap-2">
            {STYLE_PRESETS.map((style) => (
              <button
                key={style.id}
                type="button"
                onClick={() => setSelectedStyle(style)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border",
                  selectedStyle.id === style.id
                    ? "bg-white/[0.1] border-white/[0.2] text-white"
                    : "bg-white/[0.03] border-white/[0.06] text-[#888] hover:bg-white/[0.06] hover:text-white",
                )}
              >
                {STYLE_ICONS[style.id]}
                {style.name}
              </button>
            ))}
          </div>
        </div>

        {/* Position Presets */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-[#888]">Position</label>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {POSITION_PRESETS.map((position) => {
              const isSelected = selectedPosition?.id === position.id;
              return (
                <button
                  key={position.id}
                  type="button"
                  onClick={() =>
                    setSelectedPosition(isSelected ? null : position)
                  }
                  className={cn(
                    "flex flex-col items-center gap-2 p-3 rounded-xl text-xs font-medium transition-all border",
                    isSelected
                      ? "bg-white/[0.1] border-white/[0.2] text-white"
                      : "bg-white/[0.03] border-white/[0.06] text-[#888] hover:bg-white/[0.06] hover:text-white",
                  )}
                >
                <div
                  className={cn(
                    "w-full aspect-square rounded-lg overflow-hidden flex items-center justify-center bg-white/[0.05]",
                  )}
                >
                  {position.thumbnail && (
                    <img
                      src={position.thumbnail}
                      alt={position.name}
                      className={cn(
                        "w-3/4 h-3/4 object-contain transition-opacity",
                        isSelected ? "opacity-100" : "opacity-40",
                      )}
                      loading="lazy"
                    />
                  )}
                </div>
                  {position.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Advanced Options */}
        <div className="border border-white/[0.06] rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-between w-full px-4 py-3 text-sm text-[#888] hover:text-white transition-colors"
          >
            <span className="font-medium">Advanced Options</span>
            <ChevronDown
              className={cn(
                "size-4 transition-transform duration-200",
                showAdvanced && "rotate-180",
              )}
            />
          </button>
          <div
            className={cn(
              "grid transition-all duration-300 ease-in-out",
              showAdvanced ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
            )}
          >
            <div className="overflow-hidden">
              <div className="px-4 pb-4 space-y-4 border-t border-white/[0.06] pt-4">
                <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-white/[0.12] via-white/[0.04] to-white/[0.08] w-full md:w-auto">
                  <ImageDimensionsDropdown
                    value={dimensions}
                    onChange={setDimensions}
                  />
                </div>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-[#888]">
                    Prompt Optimization
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={promptOptimization}
                    onClick={() => setPromptOptimization(!promptOptimization)}
                    className={cn(
                      "relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200",
                      promptOptimization ? "bg-violet-600" : "bg-white/[0.1]",
                    )}
                  >
                    <span
                      className={cn(
                        "pointer-events-none inline-block size-5 rounded-full bg-white shadow-lg transform transition-transform duration-200",
                        promptOptimization ? "translate-x-5" : "translate-x-0",
                      )}
                    />
                  </button>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Generate Button - Desktop */}
        <div className="hidden md:block">
          <GenerateButton
            disabled={!prompt.trim()}
            className="w-full"
            onClick={handleGenerate}
          >
            <ImageIcon className="size-5" />
            Generate Image ({IMAGE_CREDIT_COST} credits)
          </GenerateButton>
        </div>
      </div>

      {/* Fixed bottom bar - Mobile only */}
      <MobileBottomBar>
        <GenerateButton
          disabled={!prompt.trim()}
          className="w-full"
          onClick={handleGenerate}
        >
          <ImageIcon className="size-5" />
          Generate Image ({IMAGE_CREDIT_COST} credits)
        </GenerateButton>
      </MobileBottomBar>

      <UpgradePlansModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, preset: "no-credits" })}
        preset={upgradeModal.preset}
        presetContext={{
          creditsNeeded: upgradeModal.creditsNeeded,
          currentCredits: credits,
        }}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
}
